import { StepButton } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { memo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InlineTextButton from '../../components/BasicUI/Button/InlineTextButton';
import IconArrowHead from '../../components/Icons/IconArrowHead/IconArrowHead';
import IconLocationPin from '../../components/Icons/IconLocationPin';
import IconStar from '../../components/Icons/IconStar';
import ProgressiveImage from '../../components/Image/ProgressiveImage';
import Modal from '../../components/ModalNew/Modal';
import { petrol } from '../../styles/mui/colors';
import { REQUEST_BOOKING } from '../../util/transaction.default';
import { LINE_ITEM_NIGHT } from '../../util/types';
import ChangeDatesModal from './ChangeDatesModal';
import ChangeGuestsModal from './ChangeGuestsModal';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';

// Wide enough, with an even split, that the summary column stops wrapping word by word
// while the voucher field in the stepper column keeps its full placeholder.
const DIALOG_WIDTH = 'min(920px, 92vw)';

const containerStyles = {
  maxWidth: { xs: '100%', md: DIALOG_WIDTH },
  width: { xs: '100%', md: DIALOG_WIDTH },
  maxHeight: { xs: '100%', md: '90vh' },
};

const BookingModalComponent = ({
  bookingDates,
  bookingLimit,
  categoryLabel,
  currentListing,
  distanceToLocation,
  fetchTimeSlotsInProgress,
  fetchLineItemsInProgress,
  getGuestDisplayValue,
  guestCount,
  guestOptions,
  lineItems = [],
  minimumNights = 1,
  onClose,
  onGuestCountChange,
  onBookingDatesChange,
  onMonthChange,
  onSubmit,
  open,
  showAnnualPassError,
  steps,
  timeSlots,
}) => {
  const intl = useIntl();

  const [activeStep, setActiveStep] = useState(0);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [openToggle, setOpenToggle] = useState(false);

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  if (!currentListing) return null;

  const modalTitle = intl.formatMessage({ id: 'BookingDatesForm.modalTitle' });
  const nextButtonLabel = intl.formatMessage({ id: 'BookingDatesForm.nextButtonLabel' });
  const backButtonLabel = intl.formatMessage({ id: 'BookingDatesForm.backButtonLabel' });
  const checkInLabel = intl.formatMessage({ id: 'BookingDatesForm.checkInLabel' });
  const checkOutLabel = intl.formatMessage({ id: 'BookingDatesForm.checkOutLabel' });
  const changeDatesLabel = intl.formatMessage({ id: 'BookingDatesForm.changeDatesLabel' });
  const changeGuestsLabel = intl.formatMessage({ id: 'BookingDatesForm.changeGuestsLabel' });
  const noDatesSelectedLabel = intl.formatMessage({ id: 'BookingDatesForm.noDatesSelectedLabel' });
  const isBookingRequest = currentListing.attributes.publicData.category === REQUEST_BOOKING;
  const bookNowButtonLabel = isBookingRequest
    ? intl.formatMessage({ id: 'BookingDatesForm.requestAndPayButtonLabel' })
    : intl.formatMessage({ id: 'BookingDatesForm.bookNowButtonLabel' });
  const priceBreakdownTitle = intl.formatMessage({ id: 'BookingDatesForm.priceBreakdownTitle' });

  const firstImage = currentListing.images[0];
  const imageUrl = firstImage?.attributes.variants['square-small']?.url || '';
  const { rating = 0, ratingCount = 0 } = currentListing.attributes.metadata || {};
  const locationPlace = currentListing.attributes.publicData.locationPlace;

  return (
    <Modal
      containerStyles={containerStyles}
      onClose={onClose}
      open={open}
      title={modalTitle}
    >
      <Grid container spacing={{ xs: 2, md: 4 }} sx={{ height: '100%', minHeight: { md: '50vh' }, paddingBottom: {xs: 4, md: 0}}}>
        <Grid display="flex" flexDirection="column" size={{ xs: 12, md: 6 }}>
          <Stack direction="column" height="100%" justifyContent="space-between" spacing={4}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map(({ label, component }, index) => (
                <Step completed={false} key={label}>
                  <StepButton disabled={false} onClick={() => setActiveStep(index)}>{label}</StepButton>
                  <StepContent>
                    <Stack direction="column" spacing={2}>
                      {component}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Stack direction="row" gap={2}>
                          {activeStep > 0 ? (
                            <Button onClick={handleBack} variant="outlined">{backButtonLabel}</Button>
                          ) : null}
                          {activeStep < steps.length - 1 ? (
                            <Button onClick={handleNext} variant="contained">{nextButtonLabel}</Button>
                          ) : null}
                        </Stack>
                      </Box>
                    </Stack>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            <Stack direction="column" spacing={2} width="100%">
              {showAnnualPassError ? (
                <Alert severity="warning">
                  {showAnnualPassError}
                </Alert>
              ) : null}
              <Button
                color="success"
                disabled={fetchLineItemsInProgress || !!showAnnualPassError}
                fullWidth
                loading={fetchLineItemsInProgress}
                onClick={onSubmit}
                size="large"
                variant="contained"
              >
                {bookNowButtonLabel}
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ borderRadius: 1, overflow: 'hidden', maxWidth: '128px' }}>
                <ProgressiveImage
                  addHelmet={false}
                  disableBlur={true}
                  src={imageUrl}
                  title={currentListing.attributes.title}
                />
              </Box>
              <Stack direction="column" spacing={1}>
                <Typography variant="h5">{currentListing.attributes.title}</Typography>
                <Chip
                  icon={<IconLocationPin sx={{ color: petrol.main }} />}
                  label={<Typography component="span" variant="body2">
                    {locationPlace}
                    {distanceToLocation ? (
                      <Typography component="span" variant="body1">
                        {distanceToLocation ? <span>&nbsp;</span> : null}
                        <FormattedMessage
                          id="ListingPage.distanceToListing"
                          values={{ distance: distanceToLocation }}
                        />
                      </Typography>
                    ) : null}
                  </Typography>}
                  sx={{
                    justifyContent: 'flex-start',
                    '& .MuiChip-label': {
                      whiteSpace: 'unset',
                      overflow: 'unset',
                      textOverflow: 'unset',
                    },
                    '& .MuiSvgIcon-root': {
                      color: petrol.main,
                    },
                    '& .MuiChip-icon': {
                      marginLeft: 0,
                    },
                  }}
                  variant="info"
                />
                <Stack direction="row" spacing={1}>
                  <Box alignItems="center" display="flex" flexDirection="row">
                    <IconStar filled />
                    <Typography variant="body1">{rating ? (rating / 100).toFixed(2) : 0}</Typography>
                    <Typography ml={1} variant="body1">
                      ({ratingCount ? ratingCount : 0})
                    </Typography>
                  </Box>
                  <Box>•</Box>
                  <Typography>{categoryLabel}</Typography>
                </Stack>
                <Typography color="text.secondary" variant="body2">
                  <FormattedMessage
                    id="ListingPage.availableSeats.text"
                    values={{ seats: currentListing.attributes.publicData.amountOfSeats || 1 }}
                  />
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={4}>
                {bookingDates[0] ? (
                  <Stack direction="column" spacing={0.5}>
                    <Typography color="text.secondary" variant="caption">{checkInLabel}</Typography>
                    <Typography variant="body2">{moment(bookingDates[0]).format('ddd, MMM DD, YYYY')}</Typography>
                  </Stack>
                ) : null}
                {bookingDates[1] ? (
                  <Stack direction="column" spacing={0.5}>
                    <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">{checkOutLabel}</Typography>
                    <Typography variant="body2">{moment(bookingDates[1]).format('ddd, MMM DD, YYYY')}</Typography>
                  </Stack>
                ) : null}
                {!bookingDates[0] && !bookingDates[1] ? (
                  <Typography color="text.secondary" variant="body2">{noDatesSelectedLabel}</Typography>
                ) : null}
              </Stack>
              <Button
                color="secondary"
                onClick={() => setDateModalOpen(true)}
                size="small"
                variant="text"
              >
                {changeDatesLabel}
              </Button>
            </Stack>
            <Divider />
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Typography
                variant="body2">{getGuestDisplayValue(guestCount) || intl.formatMessage({ id: 'BookingDatesForm.guestCountLabel' })}</Typography>
              <Button
                color="secondary"
                onClick={() => setGuestModalOpen(true)}
                size="small"
                variant="text"
              >
                {changeGuestsLabel}
              </Button>
            </Stack>
            <Divider />
            <Box sx={{ mt: 2 }}>
              <InlineTextButton
                fullWidth
                onClick={(e) => {
                  e.preventDefault();
                  setOpenToggle(!openToggle);
                }}
                sx={{ textDecoration: 'none' }}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Typography
                    color="grey.A400"
                    component="h3"
                    variant="h6"
                  >
                    {priceBreakdownTitle}
                  </Typography>
                  <IconArrowHead direction={openToggle ? 'up' : 'down'} />
                </Stack>
              </InlineTextButton>
              <EstimatedBreakdownMaybe
                bookingData={{
                  startDate: bookingDates[0],
                  endDate: bookingDates[1],
                  unitType: LINE_ITEM_NIGHT,
                }}
                currentListing={currentListing}
                lineItems={lineItems}
                showBookingPeriod={false}
                toggleState={openToggle}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <ChangeDatesModal
        bookingDates={bookingDates}
        bookingLimit={bookingLimit}
        fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
        minimumNights={minimumNights}
        onChange={onBookingDatesChange}
        onClose={() => setDateModalOpen(false)}
        onMonthChange={onMonthChange}
        open={dateModalOpen}
        timeSlots={timeSlots}
      />
      <ChangeGuestsModal
        guestOptions={guestOptions}
        onChange={onGuestCountChange}
        onClose={() => setGuestModalOpen(false)}
        open={guestModalOpen}
        totalMaximum={currentListing.attributes.publicData.guestOptions?.maxGuestCount || 2}
        value={guestCount}
      />
    </Modal>
  );
};

const BookingModal = memo(BookingModalComponent);

export default BookingModal;
